import { EyeSlashIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDismissible } from '../../../../../hooks/learner/useMemberConfig';

interface ReminderCalloutProps {
  storageKey: string;
  title: string;
  message: string;
}

/**
 * ReminderCallout — orange-tinted info callout used in the evaluation and
 * feedback flows. Dismissal persists to the member's `/me/config` (so it
 * carries across devices); `storageKey` is the config key.
 */
export default function ReminderCallout({
  storageKey,
  title,
  message,
}: ReminderCalloutProps) {
  const { dismissed, isLoading, dismiss } = useDismissible(storageKey);

  const handleDismiss = () => {
    dismiss();
  };

  return (
    <AnimatePresence>
      {!isLoading && !dismissed && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
          className="overflow-hidden"
        >
          <div className="relative flex flex-col items-center text-center gap-[22px] px-[17px] pt-5 pb-6 rounded-2xl bg-secondary-500/10 border border-secondary-500/40">
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss reminder"
              className="absolute top-3 right-3 flex items-center justify-center w-8 h-8 p-0 rounded-full border border-secondary-500 bg-transparent text-secondary-500 cursor-pointer transition-colors hover:bg-secondary-500/10"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>

            <div className="flex flex-col gap-4 mt-3 max-w-[560px]">
              <p className="m-0 font-sans font-bold text-xl leading-7 text-text-primary">
                {title}
              </p>
              <p className="m-0 font-sans font-normal text-[15px] leading-[22px] text-text-secondary whitespace-pre-wrap">
                {message}
              </p>
            </div>

            <button
              type="button"
              onClick={handleDismiss}
              className="inline-flex items-center gap-2 min-h-9 px-3.5 py-1.5 rounded-full border border-border-primary bg-transparent text-text-primary font-sans font-bold text-[13px] leading-[22px] cursor-pointer transition-colors hover:bg-grey-500/10"
            >
              <EyeSlashIcon className="w-[18px] h-[18px]" aria-hidden="true" />
              Never show it again
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
